import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  HStack,
  useColorModeValue,
  useToast,
  Spinner,
  Text,
  Button,
} from "@chakra-ui/react";
import LightBulbIcon from "./LightBulbIcon";
import { LuMoon, LuCloud, LuSun, LuPower } from "react-icons/lu";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";

// Define every scene mode: power state & color
const SCENE_CONFIG = {
  bedtime: { power: "ON", color: "#6C5B7B" }, // purple-ish
  chill: { power: "ON", color: "#00AEC7" }, // teal
  bright: { power: "ON", color: "#FFD56B" }, // warm yellow
  red: { power: "ON", color: "#FF4136" },
  green: { power: "ON", color: "#2ECC40" },
  blue: { power: "ON", color: "#0074D9" },
  yellow: { power: "ON", color: "#FFDC00" },
  "all off": { power: "OFF", color: "#00000000" },
};

const sceneMenuItems = [
  { label: "Bedtime", value: "bedtime", icon: <LuMoon /> },
  { label: "Chill", value: "chill", icon: <LuCloud /> },
  { label: "Bright", value: "bright", icon: <LuSun /> },
];

export default function LightBulbButton({ initialScene = "off" }) {
  const toast = useToast();
  const [scene, setScene] = useState(initialScene);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const backgroundColor = useColorModeValue("white", "gray.700");
  const iconColor = useColorModeValue("#4b635e", "#E9A6A6");

  // Derive power + color from SCENE_CONFIG
  const power = SCENE_CONFIG[scene]?.power ?? SCENE_CONFIG["all off"].power;
  const color = SCENE_CONFIG[scene]?.color ?? SCENE_CONFIG["all off"].color;
  const isOn = power === "ON";

  // Firestore listener
  useEffect(() => {
    const ref = doc(db, "scenes", "scene");
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setError("Scene document not found.");
        } else {
          const data = snap.data();
          if (typeof data.scene === "string" && data.scene in SCENE_CONFIG) {
            setScene(data.scene);
            setError(null);
          } else {
            setError("Invalid scene in database.");
          }
        }
        setIsLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Connection error.");
        setIsLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  // Send new scene to Firestore
  const handleSelect = async (mode) => {
    if (!(mode in SCENE_CONFIG)) return;
    setScene(mode); // optimistic UI
    try {
      await updateDoc(doc(db, "scenes", "scene"), { scene: mode });
      toast({
        title: `Mode set: ${mode}`,
        status: "success",
        duration: 1000,
        isClosable: false,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to update scene.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Loading / error state
  if (isLoading) {
    return (
      <Button
        as={IconButton}
        aria-label="Toggle light bulb"
        icon={<Spinner size="sm" />}
        borderRadius="lg"
        boxShadow="lg"
        backdropFilter="blur(5px)"
        size="md"
      />
    );
  }
  if (error) {
    console.error(error);
    return (
      <Button
        as={IconButton}
        aria-label="Toggle light bulb"
        icon={<Text color={iconColor}>X</Text>}
        borderRadius="lg"
        boxShadow="lg"
        backdropFilter="blur(5px)"
        size="md"
      />
    );
  }

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label="Toggle light bulb"
        icon={<LightBulbIcon isOn={isOn} onColor={color ?? "#999"} />}
        borderRadius="lg"
        boxShadow="lg"
        backdropFilter="blur(5px)"
        size="md"
      />

      <MenuList bg={backgroundColor} boxShadow="md" borderRadius="md" p={1}>
        {/* Scenes row */}
        <HStack w="full" spacing={0} justify="space-between" px={2} py={1}>
          {sceneMenuItems.map((item) => (
            <MenuItem
              key={item.value}
              flex="1"
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              onClick={() => handleSelect(item.value)}
              px={0}
              py={2}
              fontSize="sm"
            >
              {item.icon}
              {item.label}
            </MenuItem>
          ))}
        </HStack>

        <MenuDivider />

        {/* Color picker row */}
        <MenuItem px={0}>
          <HStack w="full" justify="space-between" px={4} py={2}>
            {["red", "green", "blue", "yellow"].map((c) => (
              <Box
                key={c}
                h="20px"
                w="20px"
                bg={SCENE_CONFIG[c].color}
                cursor="pointer"
                onClick={() => handleSelect(c)}
              />
            ))}
          </HStack>
        </MenuItem>

        <MenuDivider />

        {/* Off */}
        <MenuItem onClick={() => handleSelect("all off")}>
          <Box flex="1">Off</Box>
          <LuPower />
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
